/*
 * Lemonade Training Oy, copy rights, all right reserved.
 */
import {
  collection,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentSnapshot,
  type Query,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { firebaseDB } from "@lemonadetraining/web-shared";
import { isMembershipRequest, type MembershipRequest } from "@lemonadetraining/contracts";

export const MEMBERSHIP_REQUESTS = "MembershipRequests";

export const REQUEST_PAGE_SIZE = 20;

/** One over the display cap, so `> 20` is knowable without an unbounded read. */
export const BADGE_QUERY_LIMIT = 21;
export const BADGE_DISPLAY_CAP = 20;

interface PageOptions {
  pageSize?: number;
  after?: DocumentSnapshot | null;
}

/*
 * Every MembershipRequests read in the dashboard is built here, because the
 * Firestore emulator does not enforce composite indexes: a filter added or
 * reordered at a call site passes CI and then fails only in production. The
 * filter order below mirrors the shipped indexes field-for-field:
 *   (boxID, category, status, createdAt DESC)
 *   (boxID, category, createdAt DESC)
 */
function requestsCollection() {
  return collection(firebaseDB, MEMBERSHIP_REQUESTS);
}

function paged(base: Query, { pageSize = REQUEST_PAGE_SIZE, after = null }: PageOptions): Query {
  return after ? query(base, startAfter(after), limit(pageSize)) : query(base, limit(pageSize));
}

/** Requests that need an answer from the gym. */
export function decisionQueueQuery(boxID: string, options: PageOptions = {}): Query {
  return paged(
    query(
      requestsCollection(),
      where("boxID", "==", boxID),
      where("category", "==", "decision"),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    ),
    options
  );
}

/**
 * Notices the gym cannot decide, only respond to. Deliberately unfiltered by
 * status so `effective`, `contested` and `upheld` all stay on screen — a
 * dispute the gym raised must not vanish the moment its status moves.
 */
export function awarenessQueueQuery(boxID: string, options: PageOptions = {}): Query {
  return paged(
    query(
      requestsCollection(),
      where("boxID", "==", boxID),
      where("category", "==", "awareness"),
      orderBy("createdAt", "desc")
    ),
    options
  );
}

/** Same shape as the decisions page-1 query, capped one past the display cap. */
export function pendingDecisionCountQuery(boxID: string): Query {
  return decisionQueueQuery(boxID, { pageSize: BADGE_QUERY_LIMIT });
}

export function formatBadgeCount(count: number): string {
  return count > BADGE_DISPLAY_CAP ? `${BADGE_DISPLAY_CAP}+` : String(count);
}

/**
 * Turn raw documents into requests, dropping anything the published guard does
 * not recognise — and saying so.
 *
 * The guard checks `category`, `status` and `requestType` against the shipped
 * unions, so a value written before its type is published would be dropped
 * here. `functions/` is plain JavaScript with no typecheck, so that drift is
 * reachable, and a request vanishing from the owner's queue with no error
 * anywhere is exactly the silent failure this project keeps paying for. One
 * line per snapshot, counts only — never an id, never a uid.
 */
export function readRequestDocs(
  docs: QueryDocumentSnapshot[],
  surface: string
): MembershipRequest[] {
  const items: MembershipRequest[] = [];
  let dropped = 0;
  docs.forEach((d) => {
    const candidate = { ...d.data(), id: d.id };
    if (isMembershipRequest(candidate)) {
      items.push(candidate);
    } else {
      dropped += 1;
    }
  });
  if (dropped > 0) {
    console.warn("membership requests dropped by shape guard", { surface, dropped });
  }
  return items;
}
